import React, { useState, useEffect } from 'react';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { obtenerEstilosAdmin, guardarEstilos, ESTILOS_DEFAULT } from '../../services/themeService';
import { usePlan } from '../context/PlanContext';
import './ConfiguradorTienda.css';

const FUENTES_DISPONIBLES = [
    { valor: "'Inter', sans-serif", nombre: 'Inter (moderna)' },
    { valor: "'Poppins', sans-serif", nombre: 'Poppins (redondeada)' },
    { valor: "'Roboto', sans-serif", nombre: 'Roboto (clásica)' },
    { valor: "'Montserrat', sans-serif", nombre: 'Montserrat (elegante)' },
    { valor: "Georgia, serif", nombre: 'Georgia (con serifa)' },
];

const RADIOS_DISPONIBLES = [
    { valor: '4px', nombre: 'Recto' },
    { valor: '12px', nombre: 'Redondeado' },
    { valor: '24px', nombre: 'Muy redondeado' },
];

function ShopifyCard({ icono, titulo, children }) {
    return (
        <div className="shopify-card">
            <div className="shopify-card-header">
                <div className="shopify-card-icon"><i className={icono}></i></div>
                <h6 className="shopify-card-title">{titulo}</h6>
            </div>
            {children}
        </div>
    );
}

function ColorPicker({ label, valor, onChange }) {
    return (
        <div className="color-picker-wrap">
            <div className="color-picker-btn">
                <div className="color-swatch" style={{ background: valor }}></div>
                <input type="color" value={valor || '#000000'} onChange={(e) => onChange(e.target.value)} />
            </div>
            <div>
                <div className="color-picker-label">{label}</div>
                <div className="color-picker-value">{valor}</div>
            </div>
        </div>
    );
}

function ConfiguradorPedidosWeb() {
    const { tienePedidosWeb } = usePlan();
    const [estilos, setEstilos] = useState(ESTILOS_DEFAULT);
    const [loading, setLoading] = useState(false);
    const [cargando, setCargando] = useState(true);
    const [cambiosPendientes, setCambiosPendientes] = useState(false);

    useEffect(() => {
        const cargar = async () => {
            setCargando(true);
            const data = await obtenerEstilosAdmin();
            setEstilos(data);
            setCargando(false);
        };
        cargar();
    }, []);

    const pw = { ...ESTILOS_DEFAULT.pedidos_web, ...(estilos.pedidos_web || {}) };

    const set = (campo, valor) => {
        setEstilos(prev => ({
            ...prev,
            pedidos_web: { ...ESTILOS_DEFAULT.pedidos_web, ...(prev.pedidos_web || {}), [campo]: valor }
        }));
        setCambiosPendientes(true);
    };

    const handleGuardar = async () => {
        setLoading(true);
        try {
            const res = await guardarEstilos(estilos);
            if (res && (res.status === 'ok' || res.status === 'success')) {
                toast.success('Cambios guardados');
                setCambiosPendientes(false);
            } else {
                toast.error('Error al guardar');
            }
        } catch (err) {
            toast.error('Error de conexión al guardar');
        } finally {
            setLoading(false);
        }
    };

    if (!tienePedidosWeb) {
        return (
            <div className="config-tienda">
                <div className="config-header">
                    <div className="config-header-left">
                        <div className="config-header-icon"><i className="bi bi-cart-check"></i></div>
                        <div>
                            <h1 className="config-header-title">Pedidos Web</h1>
                            <p className="config-header-subtitle">Personaliza el checkout de tu tienda online.</p>
                        </div>
                    </div>
                </div>
                <div className="config-content">
                    <div className="config-content-inner">
                        <div className="info-box">
                            <div className="info-box-icon"><i className="bi bi-lock-fill"></i></div>
                            <div>
                                <strong>Pedidos Web no está incluido en tu plan actual.</strong>
                                <div className="text-muted small mt-1">Disponible en los planes Menú QR y Restaurante.</div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    if (cargando) {
        return <div className="text-center py-5 text-muted">Cargando configuración...</div>;
    }

    return (
        <div className="config-tienda">
            <ToastContainer position="bottom-center" autoClose={2500} hideProgressBar />

            <div className="config-header">
                <div className="config-header-left">
                    <div className="config-header-icon"><i className="bi bi-cart-check"></i></div>
                    <div>
                        <h1 className="config-header-title">Pedidos Web</h1>
                        <p className="config-header-subtitle">Personaliza los colores y el estilo del checkout de tu tienda online.</p>
                    </div>
                </div>
                <div className="config-header-right">
                    {cambiosPendientes && (
                        <span className="config-unsaved-badge">
                            <i className="bi bi-exclamation-circle"></i> Sin guardar
                        </span>
                    )}
                    <button className="config-btn-save" onClick={handleGuardar} disabled={loading}>
                        {loading
                            ? <span className="spinner-border spinner-border-sm" />
                            : <i className="bi bi-check-lg"></i>}
                        Guardar
                    </button>
                </div>
            </div>

            <div className="config-body config-body-flat">
                <div className="config-content">
                    <div className="config-content-inner">
                        <div className="row g-4">

                            {/* COLUMNA IZQUIERDA: FORMULARIOS */}
                            <div className="col-lg-7">
                                <ShopifyCard icono="bi bi-palette" titulo="Colores principales">
                                    <div className="config-grid-2">
                                        <ColorPicker label="Color primario" valor={pw.color_primario} onChange={(v) => set('color_primario', v)} />
                                        <ColorPicker label="Color secundario" valor={pw.color_secundario} onChange={(v) => set('color_secundario', v)} />
                                        <ColorPicker label="Fondo" valor={pw.color_fondo} onChange={(v) => set('color_fondo', v)} />
                                        <ColorPicker label="Texto" valor={pw.color_texto} onChange={(v) => set('color_texto', v)} />
                                    </div>
                                </ShopifyCard>

                                <ShopifyCard icono="bi bi-square" titulo="Botones">
                                    <div className="config-grid-2">
                                        <ColorPicker label="Botón principal (fondo)" valor={pw.color_btn_primario} onChange={(v) => set('color_btn_primario', v)} />
                                        <ColorPicker label="Botón principal (texto)" valor={pw.color_btn_texto} onChange={(v) => set('color_btn_texto', v)} />
                                        <ColorPicker label="Botón continuar (fondo)" valor={pw.btn_continuar_bg} onChange={(v) => set('btn_continuar_bg', v)} />
                                        <ColorPicker label="Botón continuar (texto)" valor={pw.btn_continuar_texto} onChange={(v) => set('btn_continuar_texto', v)} />
                                    </div>
                                </ShopifyCard>

                                <ShopifyCard icono="bi bi-tags" titulo="Categorías del menú">
                                    <div className="config-grid-2">
                                        <ColorPicker label="Activa (fondo)" valor={pw.categoria_activa_bg} onChange={(v) => set('categoria_activa_bg', v)} />
                                        <ColorPicker label="Activa (texto)" valor={pw.categoria_activa_texto} onChange={(v) => set('categoria_activa_texto', v)} />
                                        <ColorPicker label="Inactiva (fondo)" valor={pw.categoria_inactiva_bg} onChange={(v) => set('categoria_inactiva_bg', v)} />
                                        <ColorPicker label="Inactiva (texto)" valor={pw.categoria_inactiva_texto} onChange={(v) => set('categoria_inactiva_texto', v)} />
                                    </div>
                                </ShopifyCard>

                                <ShopifyCard icono="bi bi-fonts" titulo="Tipografía y forma">
                                    <div className="config-grid-2">
                                        <div>
                                            <label className="config-label">Fuente</label>
                                            <select className="config-input" value={pw.fuente} onChange={(e) => set('fuente', e.target.value)}>
                                                {FUENTES_DISPONIBLES.map(f => (
                                                    <option key={f.valor} value={f.valor}>{f.nombre}</option>
                                                ))}
                                            </select>
                                        </div>
                                        <div>
                                            <label className="config-label">Bordes</label>
                                            <select className="config-input" value={pw.border_radius_general} onChange={(e) => set('border_radius_general', e.target.value)}>
                                                {RADIOS_DISPONIBLES.map(r => (
                                                    <option key={r.valor} value={r.valor}>{r.nombre}</option>
                                                ))}
                                            </select>
                                        </div>
                                    </div>
                                </ShopifyCard>
                            </div>

                            {/* COLUMNA DERECHA: VISTA PREVIA */}
                            <div className="col-lg-5">
                                <div className="sticky-card">
                                    <div className="sticky-card-header">
                                        <span className="config-label mb-0">Vista previa</span>
                                        <span className="config-badge">Checkout</span>
                                    </div>
                                    <div className="sticky-card-body" style={{
                                        background: pw.color_fondo,
                                        color: pw.color_texto,
                                        fontFamily: pw.fuente,
                                        borderRadius: pw.border_radius_general
                                    }}>
                                        <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
                                            <span style={{
                                                background: pw.categoria_activa_bg, color: pw.categoria_activa_texto,
                                                padding: '6px 14px', borderRadius: pw.border_radius_general, fontSize: 13, fontWeight: 600
                                            }}>Hamburguesas</span>
                                            <span style={{
                                                background: pw.categoria_inactiva_bg, color: pw.categoria_inactiva_texto,
                                                padding: '6px 14px', borderRadius: pw.border_radius_general, fontSize: 13, fontWeight: 600
                                            }}>Bebidas</span>
                                            <span style={{
                                                background: pw.categoria_inactiva_bg, color: pw.categoria_inactiva_texto,
                                                padding: '6px 14px', borderRadius: pw.border_radius_general, fontSize: 13, fontWeight: 600
                                            }}>Postres</span>
                                        </div>

                                        <div style={{
                                            background: '#fff', borderRadius: pw.border_radius_general,
                                            padding: 14, marginBottom: 12, boxShadow: '0 1px 3px rgba(0,0,0,0.08)'
                                        }}>
                                            <div style={{ fontWeight: 700, marginBottom: 4, color: pw.color_texto }}>Hamburguesa Clásica</div>
                                            <div style={{ fontSize: 12, color: pw.color_secundario, marginBottom: 8 }}>Con papas fritas incluidas</div>
                                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                <span style={{ fontWeight: 700, color: pw.color_primario }}>$6.500</span>
                                                <button style={{
                                                    background: pw.color_btn_primario, color: pw.color_btn_texto,
                                                    border: 'none', padding: '6px 16px', borderRadius: pw.border_radius_general,
                                                    fontSize: 12, fontWeight: 700
                                                }}>Agregar</button>
                                            </div>
                                        </div>

                                        <button style={{
                                            width: '100%', background: pw.btn_continuar_bg, color: pw.btn_continuar_texto,
                                            border: 'none', padding: '12px', borderRadius: pw.border_radius_general,
                                            fontSize: 14, fontWeight: 700
                                        }}>Continuar al pago</button>
                                    </div>
                                </div>
                            </div>

                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default ConfiguradorPedidosWeb;
